import type { Terrain, Vec2 } from '../terrain.js';
import { resolveDashMovement, type DashMovementResult } from './dash-movement.js';

export const COMMON_DASH_MAX_DISTANCE_M = 10;
export const COMMON_DASH_COOLDOWN_S = 13;

export interface CommonDashAttemptInput {
  readonly terrain: Terrain;
  readonly origin: Vec2;
  readonly movement: Vec2;
  readonly facing?: Vec2;
  readonly requestedDistanceM?: number;
  readonly cooldownRemainingS: number;
  /** Authoritative movement availability: root/stun and movement policy. */
  readonly movementAllowed: boolean;
  readonly alive: boolean;
  readonly down: boolean;
}

export type CommonDashAttemptResult =
  | { readonly accepted: true; readonly movement: DashMovementResult; readonly cooldownRemainingS: number }
  | { readonly accepted: false; readonly reason: 'invalid_distance' | 'on_cooldown' | 'movement_locked' | 'no_direction'; readonly cooldownRemainingS: number };

/**
 * A side-effect-free activation decision. The world owns storage and tick
 * ordering; this module never grants a skill slot, damage, phasing or immunity.
 */
export function resolveCommonDashAttempt(input: CommonDashAttemptInput): CommonDashAttemptResult {
  // Undefined means omitted. Null is an explicit, malformed wire value and
  // must not become a free default-distance dash via nullish coalescing.
  const requestedM = input.requestedDistanceM === undefined ? COMMON_DASH_MAX_DISTANCE_M : input.requestedDistanceM;
  const reject = (reason: Extract<CommonDashAttemptResult, { accepted: false }>['reason']): CommonDashAttemptResult =>
    ({ accepted: false, reason, cooldownRemainingS: input.cooldownRemainingS });
  if (!Number.isFinite(requestedM) || requestedM <= 0 || requestedM > COMMON_DASH_MAX_DISTANCE_M) return reject('invalid_distance');
  if (!Number.isFinite(input.cooldownRemainingS) || input.cooldownRemainingS > 0 || input.cooldownRemainingS < 0) return reject('on_cooldown');
  if (!input.movementAllowed || !input.alive || input.down) return reject('movement_locked');
  const heading = Number.isFinite(input.movement.x) && Number.isFinite(input.movement.y) &&
    Math.hypot(input.movement.x, input.movement.y) > 1e-9 ? input.movement : input.facing;
  if (!heading || !Number.isFinite(heading.x) || !Number.isFinite(heading.y) || Math.hypot(heading.x, heading.y) <= 1e-9) return reject('no_direction');
  const movement = resolveDashMovement(input.terrain, input.origin, heading, requestedM, COMMON_DASH_MAX_DISTANCE_M);
  if (!movement) return reject('no_direction');
  return { accepted: true, movement, cooldownRemainingS: COMMON_DASH_COOLDOWN_S };
}

/**
 * Single mutation boundary for a world-owned player actor. The caller supplies
 * the world-computed movement gate and chooses the tick ordering; rejected
 * attempts cannot change either position or cooldown. No skill state is read.
 */
export interface CommonDashMutableActor {
  readonly player: { pos: Vec2; facing?: Vec2; alive: boolean; down: boolean };
  commonDashCooldownRemainingS: number;
}

export function executeCommonDashForActor(
  actor: CommonDashMutableActor,
  input: Pick<CommonDashAttemptInput, 'terrain' | 'movement' | 'requestedDistanceM' | 'movementAllowed'>,
): CommonDashAttemptResult {
  const result = resolveCommonDashAttempt({
    ...input,
    origin: actor.player.pos,
    facing: actor.player.facing,
    cooldownRemainingS: actor.commonDashCooldownRemainingS,
    alive: actor.player.alive,
    down: actor.player.down,
  });
  if (result.accepted) {
    actor.player.pos = { ...result.movement.landed };
    actor.commonDashCooldownRemainingS = result.cooldownRemainingS;
  }
  return result;
}

/** Reduces remaining time once per authoritative tick, not wall-clock time. */
export function advanceCommonDashCooldown(remainingS: number, elapsedS: number): number {
  if (!Number.isFinite(remainingS) || remainingS < 0 || !Number.isFinite(elapsedS) || elapsedS <= 0) {
    throw new Error('common dash cooldown and elapsed time must be finite and non-negative / positive');
  }
  const next = remainingS - elapsedS;
  // Avoid leaving a fractional-tick residue after exactly 13 seconds at 20 Hz.
  return next <= 1e-9 ? 0 : next;
}
